"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { config } from "@/lib/config";
import ChiMain from "@/components/main/chi-main";
import { SelfTrust } from "@/components/main/self-trust";

interface CarouselButtonProps {
  onClick: () => void;
  label: string;
}

function CarouselButton({ onClick, label, isLeft = false }: CarouselButtonProps & { isLeft?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mx-1 sm:mx-2 rounded-full bg-amber-700 p-1.5 sm:p-2 text-white shadow hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500 touch-manipulation"
      aria-label={label}
    >
      <i className={`bi text-sm sm:text-base ${isLeft ? 'bi-chevron-left' : 'bi-chevron-right'}`} aria-hidden="true"></i>
    </button>
  );
}

function CircularProgress({ progress, isPaused }: { progress: number; isPaused: boolean }) {
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative w-6 h-6">
      <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 20 20">
        {/* Background circle */}
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth="1.5"
        />
        {/* Progress circle */}
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke={isPaused ? "#fbbf24" : "#d97706"}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-100 ease-linear"
        />
      </svg>
      {/* Pause icon when paused */}
      {isPaused && (
        <div className="absolute inset-0 flex items-center justify-center">
          <i className="bi bi-pause-fill text-amber-600 text-[8px]"></i>
        </div>
      )}
    </div>
  );
}

export function HomeCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const startXRef = useRef<number | null>(null);
  const regionRef = useRef<HTMLElement | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalSlides = 6;

  const resetProgress = useCallback(() => {
    setProgress(0);
    if (progressRef.current) {
      clearInterval(progressRef.current);
    }
  }, []);

  const startProgress = useCallback(() => {
    resetProgress();
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev + (100 / 500); // 5 seconds = 5000ms, update every 10ms = 500 steps
      });
    }, 10);
    progressRef.current = interval;
  }, [resetProgress]);

  const handlePrev = useCallback(() => {
    setActiveIndex((current) => (current === 0 ? totalSlides - 1 : current - 1));
    resetProgress();
  }, [resetProgress]);

  const handleNext = useCallback(() => {
    setActiveIndex((current) => (current === totalSlides - 1 ? 0 : current + 1));
    resetProgress();
  }, [resetProgress]);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
    if (progressRef.current) {
      clearInterval(progressRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
    startProgress();
  }, [startProgress]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      handlePrev();
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      handleNext();
      return;
    }
  }, [handleNext, handlePrev]);

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    startXRef.current = event.touches[0].clientX;
    setIsDragging(true);
  }, []);

  const handleTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (startXRef.current === null) {
      setIsDragging(false);
      return;
    }
    const deltaX = event.changedTouches[0].clientX - startXRef.current;
    const threshold = 50;
    if (deltaX > threshold) handlePrev();
    if (deltaX < -threshold) handleNext();
    startXRef.current = null;
    setIsDragging(false);
  }, [handleNext, handlePrev]);

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // Only start dragging if clicking on empty space, not on links
    if ((event.target as HTMLElement).tagName === 'A' || (event.target as HTMLElement).closest('a')) {
      return;
    }
    startXRef.current = event.clientX;
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (startXRef.current === null) {
      setIsDragging(false);
      return;
    }
    // Only handle drag if it was a significant movement
    const deltaX = event.clientX - startXRef.current;
    const threshold = 50;
    if (Math.abs(deltaX) > threshold) {
      event.preventDefault();
      if (deltaX > threshold) handlePrev();
      if (deltaX < -threshold) handleNext();
    }
    startXRef.current = null;
    setIsDragging(false);
  }, [handleNext, handlePrev]);

  useEffect(function autoplayEffect() {
    if (isPaused || isDragging) return;
    
    startProgress();
    
    const id = setInterval(() => {
      setActiveIndex((current) => (current === totalSlides - 1 ? 0 : current + 1));
    }, 5000);
    
    return () => {
      clearInterval(id);
      if (progressRef.current) {
        clearInterval(progressRef.current);
      }
    };
  }, [isPaused, isDragging, startProgress]);

  // Start progress on mount
  useEffect(() => {
    if (!isPaused && !isDragging) {
      startProgress();
    }
  }, [isPaused, isDragging, startProgress]);

  return (
    <>

    <section
      ref={regionRef as unknown as React.RefObject<HTMLDivElement>}
      className="relative mt-8 w-full"
      role="region"
      aria-roledescription="carousel"
      aria-label="Kiemelt tartalmak karusszel"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="overflow-hidden h-[400px] sm:h-[500px]">
        <div
          className="w-full select-none relative h-full"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {/* 1. az-en-tortenetem */}
          <div className={`absolute inset-0 w-full transition-opacity duration-500 ease-in-out ${activeIndex === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex h-full w-full cursor-auto justify-center items-center overflow-hidden px-2 sm:px-0">
              <a
                href={`${config.basePath}/kapcsolat/az-en-tortenetem`}
                target={"_blank"}
                rel="noreferrer"
                className="group relative block w-full max-w-[650px]"
              >
                <img
                  src={`${config.basePath}/images/az-en-tortenetem.jpg`}
                  alt='"Az én történetem" képregényes művészetterápiás foglalkozás'
                  className="w-full h-auto transition-transform duration-500 ease-in-out group-hover:scale-105"
                ></img>
              </a>
            </div>
          </div>

          {/* 2. uj-tavaszi-csoport */}
          <div className={`absolute inset-0 w-full transition-opacity duration-500 ease-in-out ${activeIndex === 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex h-full w-full justify-center items-center px-2 sm:px-0">
              <a
                href={`${config.basePath}/kapcsolat/uj-tavaszi-csoport`}
                target={"_blank"}
                rel="noreferrer"
                className="block w-full max-w-[650px]"
              >
                <img
                  src={`${config.basePath}/images/tavaszi-csoport.jpg`}
                  alt="Tavaszi művészetterápiás csoport"
                  className="w-full h-auto"
                ></img>
              </a>
            </div>
          </div>

          {/* 3. macstar 2025 */}
          <div className={`absolute inset-0 w-full transition-opacity duration-500 ease-in-out ${activeIndex === 2 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex h-full w-full animate-wiggle flex-col items-center justify-center animate-infinite px-2 sm:px-0">
              <a
                href={`${config.basePath}/2025`}
                target={"_blank"}
                rel="noreferrer"
                className="block w-full max-w-[650px]"
              >
                <img
                  src="/images/macstar2.jpg"
                  alt="2025 logo"
                  className="w-full h-auto"
                />
              </a>
            </div>
          </div>

          {/* 4. csi-festes (ugyanazzal a belső logikával mint a ChiMain) */}
          <div className={`absolute inset-0 w-full transition-opacity duration-500 ease-in-out ${activeIndex === 3 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex h-full w-full justify-center items-center px-2 sm:px-0">
              <ChiMain />
            </div>
          </div>

          {/* 5. onbizalom (ugyanazzal a belső logikával mint a SelfTrust) */}
          <div className={`absolute inset-0 w-full transition-opacity duration-500 ease-in-out ${activeIndex === 4 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex h-full w-full justify-center items-center px-2 sm:px-0">
              <SelfTrust className="mx-auto w-full max-w-[680px]" />
            </div>
          </div>

          {/* 6. spontan */}
          <div className={`absolute inset-0 w-full transition-opacity duration-500 ease-in-out ${activeIndex === 5 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex h-full w-full justify-center items-center px-2 sm:px-0">
              <a
                href={`${config.basePath}/kapcsolat/spontan`}
                target={"_blank"}
                rel="noreferrer"
                className="block w-full max-w-[650px]"
              >
                <img
                  src={`${config.basePath}/images/spontan.jpg`}
                  alt="Spontán Művészetterápia alkalmak"
                  className="w-full h-auto"
                ></img>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="pointer-events-none flex w-full max-w-[650px] justify-between">
          <div className="flex items-center -ml-16 pointer-events-auto">
            <CarouselButton onClick={handlePrev} label="Előző dia" isLeft={true} />
          </div>
          <div className="flex items-center -mr-16 pointer-events-auto">
            <CarouselButton onClick={handleNext} label="Következő dia" />
          </div>
        </div>
      </div>

      {/* Dots and Progress */}
      <div className="mt-4 flex justify-center items-center gap-3">
        <div className="flex">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={
                index === activeIndex
                  ? "mx-1 h-2 w-6 rounded-full bg-amber-700 touch-manipulation"
                  : "mx-1 h-2 w-2 rounded-full bg-amber-300 touch-manipulation"
              }
              aria-current={index === activeIndex ? "true" : undefined}
              aria-label={`Ugrás a(z) ${index + 1}. diára`}
            ></button>
          ))}
        </div>
        <CircularProgress progress={progress} isPaused={isPaused} />
      </div>

      {/* Thumbnails */}
      <div className="mt-6 flex justify-center gap-2 sm:gap-3 px-2 sm:px-0 overflow-x-auto">
        {[
          { src: `${config.basePath}/images/az-en-tortenetem.jpg`, alt: '"Az én történetem" képregényes művészetterápiás foglalkozás' },
          { src: `${config.basePath}/images/tavaszi-csoport.jpg`, alt: 'Tavaszi művészetterápiás csoport' },
          { src: '/images/macstar2.jpg', alt: '2025 logo' },
          { src: `${config.basePath}/images/csi/csifestes-tengely.jpg`, alt: 'Csí-festés' },
          { src: `${config.basePath}/images/onbizalom.png`, alt: 'Önbizalom művészetterápiás csoport' },
          { src: `${config.basePath}/images/spontan.jpg`, alt: 'Spontán Művészetterápia alkalmak' },
        ].map((thumbnail, index) => (
          <button
            key={`thumbnail-${index}`}
            type="button"
            onClick={() => {
              setActiveIndex(index);
              resetProgress();
            }}
            className={`relative flex-shrink-0 transition-all duration-300 ${
              index === activeIndex
                ? 'ring-2 ring-amber-700 ring-offset-2 scale-105'
                : 'opacity-70 hover:opacity-100 hover:scale-105'
            }`}
            aria-label={`Ugrás a(z) ${index + 1}. diára`}
          >
            <img
              src={thumbnail.src}
              alt={thumbnail.alt}
              className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md cursor-pointer"
            />
            {index === activeIndex && (
              <div className="absolute inset-0 border-2 border-amber-700 rounded-md pointer-events-none"></div>
            )}
          </button>
        ))}
      </div>
    </section>
    </>
  );
}


